import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Book, Loader2, AlertCircle, Headphones, ArrowLeft } from 'lucide-react';
import { API_URL } from '../api-config';
import { handleAuthError } from '../hooks/ValidateToken';

const SearchResultsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { usuarioId } = useParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState(location.state?.query || localStorage.getItem('searchQuery') || '');

  const handleEmptyValue = (value) => {
    return value || value === 0 ? value : "Desconocido";
  };

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.post(`${API_URL}/search`, { query });
        setResults(response.data.results || []);
      } catch (err) {
        setError('Error al buscar audiolibros. Por favor, intenta nuevamente.');
        handleAuthError(err, navigate);
      } finally {
        setLoading(false);
      }
    };

    if (query) {
      fetchResults();
    }
  }, [query]);

  const handleItemClick = (audiolibroId) => {
    localStorage.setItem('searchQuery', query);
    navigate(`/reproducir/${usuarioId}/${audiolibroId}?origin=results-list`);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center"
      role="main"
      aria-label="Resultados de búsqueda"
    >
      {/* Contenedor interno centrado */}
      <div
        className="flex flex-col items-center w-full max-w-screen-lg mx-auto px-4 md:px-6"
        role="region"
        aria-labelledby="search-results-title"
      >
        <div className="flex items-center justify-center mb-8 md:mt-8 mt-14">
          <Book className="text-purple-400 w-8 h-8 mr-3" aria-hidden="true" />
          <h1 id="search-results-title" className="text-3xl font-bold text-purple-400">
            Resultados para "{query}"
          </h1>
        </div>

        {/* Estado de carga */}
        {loading && (
          <div className="flex flex-col items-center p-12" aria-live="polite">
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin mb-4" aria-hidden="true" />
            <p className="text-purple-300">Buscando audiolibros...</p>
          </div>
        )}

        {/* Mensaje de error */}
        {error && (
          <div
            className="flex items-center justify-center p-6 bg-red-900/20 rounded-lg border border-red-500/50"
            role="alert"
          >
            <AlertCircle className="text-red-500 w-6 h-6 mr-2" aria-hidden="true" />
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Botón Volver */}
        <button
          onClick={() => navigate('/search')}
          className="fixed top-6 left-6 z-20 bg-purple-600 text-white px-4 py-2 rounded-full hover:bg-purple-700 transition-all duration-300 shadow-lg sm:text-base text-sm sm:px-4 sm:py-2"
          aria-label="Volver a la página anterior"
        >
          {/* Icono */}
          <ArrowLeft className="w-5 h-5 inline-block mr-2 sm:mr-2" aria-hidden="true" />
          {/* Texto (visible solo en pantallas grandes) */}
          <span className="hidden sm:inline">Volver</span>
        </button>

        {!loading && results.length > 0 && (
          <h2
            id="results-list-title"
            className="text-left w-full text-3xl font-semibold text-purple-300 mb-4"
            aria-label="Lista de resultados disponibles"
          >
            Lista de Resultados
          </h2>
        )}

        {/* Lista de resultados */}
        <ul className="space-y-6 w-full mb-6" aria-label="Lista de resultados de búsqueda">
          {results.map((item) => (
            <li
              key={item.id}
              className="group p-6 bg-slate-800 rounded-xl border border-purple-400/20 hover:border-purple-400/60 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10 cursor-pointer"
              role="listitem"
              tabIndex="0"
              aria-label={`Audiolibro: ${item.name}. Autor: ${handleEmptyValue(item.author)}, Género: ${handleEmptyValue(item.genre)}. Descripción: ${item.description}`}
              onClick={() => handleItemClick(item.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleItemClick(item.id);
                }
              }}
            >
              {/* Título del audiolibro */}
              <h3
                className="text-xl font-bold text-white mb-2 text-justify"
                tabIndex="0"
                aria-label={`Título del audiolibro: ${item.name}`}
              >
                {item.name}
              </h3>

              {/* Autor */}
              <p
                className="text-sm text-purple-300 text-justify"
                tabIndex="0"
                aria-label={`Autor del audiolibro: ${handleEmptyValue(item.author)}`}
              >
                Autor: {handleEmptyValue(item.author)}
              </p>

              {/* Género */}
              <p
                className="text-sm text-purple-300 text-justify"
                tabIndex="0"
                aria-label={`Género del audiolibro: ${handleEmptyValue(item.genre)}`}
              >
                Género: {handleEmptyValue(item.genre)}
              </p>

              {/* Descripción */}
              <p
                className="text-sm text-gray-400 mt-2 text-justify"
                tabIndex="0"
                aria-label={`Descripción del audiolibro: ${item.description}`}
              >
                {item.description}
              </p>

              {/* Ícono de audiolibro */}
              <div className="mt-4 flex justify-end">
                <Headphones
                  className="w-6 h-6 text-purple-400 group-hover:scale-110 transition-transform"
                  aria-hidden="true"
                  role="presentation"
                />
              </div>
            </li>
          ))}
        </ul>

        {/* Mensaje cuando no hay resultados */}
        {results.length === 0 && !loading && !error && (
          <div className="text-center py-12" aria-live="polite">
            <p className="text-purple-300 text-lg">No se encontraron audiolibros que coincidan con tu búsqueda.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsList;